import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import {useFieldArray, useFormContext} from "react-hook-form";
import {useOrgSettings} from "../../context/OrgSettingsProvider";
import {CustomSelect, CustomTextField} from "../../components/forms";
import {Grid, Button, Typography} from "@mui/material";
import {DeleteButton} from "../../components/buttons";
import ColorsWithPreview from "./ColorsWithPreview";
import {
  conditionFieldTypeDisplay,
  metricMeasureAgainstDisplay,
} from "./SchemaHelpers";
import {useSchemaFieldStyles} from "./schemaStyles";
import {contains, map, reject, sortBy} from "underscore";

const getConditionTypeOptions = (conditionTypesData) => {
  if (!conditionTypesData?.conditionType?.enumValues) {
    return [];
  }

  const excludedConditionTypes = ["Country", "State"];

  const filteredOptions = reject(
    conditionTypesData.conditionType.enumValues,
    (item) => contains(excludedConditionTypes, item.name),
  );

  const optionsWithFieldType = map(filteredOptions, (item) => ({
    ...item,
    fieldType: conditionFieldTypeDisplay(item.name),
  }));

  return sortBy(optionsWithFieldType, "fieldType");
};

const ListOption = ({classes, field, index, remove}) => (
  <Grid
    container
    item
    spacing={1}
    direction="row"
    justifyContent="flex-start"
    alignItems="stretch"
    key={`${field.id}-${index}`}
    className={classes.schemaListItem}
  >
    <CustomTextField
      label="Option Name"
      name={`listOptions.${index}.name`}
      guide="Name your Option"
      required={true}
      fullWidth={true}
    />
    <CustomTextField
      label="Option Description"
      name={`listOptions.${index}.description`}
      guide="Describe the Option"
      required={false}
      noRows={true}
      fullWidth={true}
    />
    <Grid item xs={1}>
      <DeleteButton size="large" fullWidth onClick={() => remove(index)}>
        Remove
      </DeleteButton>
    </Grid>
  </Grid>
);

ListOption.propTypes = {
  classes: PropTypes.object,
  field: PropTypes.object,
  index: PropTypes.number,
  remove: PropTypes.func,
};

const ListOptions = () => {
  const formContext = useFormContext();
  const [existingOptionsLoaded, setExistingOptionsLoaded] = useState(false);
  const {fields, append, remove} = useFieldArray({
    control: formContext.control,
    name: "listOptions",
  });

  const watchListOptions = formContext.watch("listOptions");
  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchListOptions[index],
    };
  });

  useEffect(() => {
    if (!existingOptionsLoaded && formContext.getValues("existingOptions")) {
      const optionsArrayOfObjects = [];
      formContext.getValues("existingOptions").forEach((option) => {
        optionsArrayOfObjects.push({
          id: option.id,
          name: option.name,
          description: option.description,
        });
      });
      formContext.setValue("listOptions", optionsArrayOfObjects);
      setExistingOptionsLoaded(true);
    }
  }, [formContext.getValues("existingOptions")]);

  const {classes} = useSchemaFieldStyles();

  return (
    <Grid container>
      <Typography variant="h6" className={classes.marginTop}>
        List Options
        {` (${controlledFields.length})`}
      </Typography>
      {controlledFields.map((field, index) => (
        <ListOption
          field={field}
          index={index}
          remove={remove}
          classes={classes}
          key={`${field.id}-${index}`}
        />
      ))}
      <Grid item className={classes.marginTop}>
        <Button
          color="primary"
          variant="contained"
          onClick={() => append({name: "", description: ""})}
          style={{
            marginLeft: controlledFields.length === 0 ? "8px" : undefined,
          }}
        >
          Add Option
        </Button>
      </Grid>
    </Grid>
  );
};

export const ConditionsSchemaFields = ({conditionTypesData, watch, isAdd}) => {
  const fieldType = watch("fieldType")?.name;
  return (
    <>
      <CustomTextField
        label="Condition Name"
        name="field"
        guide="Name your Condition"
        required={true}
        multi={1}
        fullWidth={true}
      />
      <CustomSelect
        label="Type"
        name="fieldType"
        optionLabel="fieldType"
        guide="Choose your Condition type"
        required={true}
        disabled={!isAdd}
        options={getConditionTypeOptions(conditionTypesData)}
      />
      {fieldType === "Enum" && <ListOptions />}
    </>
  );
};

ConditionsSchemaFields.propTypes = {
  conditionTypesData: PropTypes.object,
  watch: PropTypes.func,
  isAdd: PropTypes.bool,
};

export const ObligationTypesSchemaFields = () => (
  <>
    <CustomTextField
      label="Name"
      name="name"
      guide="Name your Procedure Type"
      required={true}
      fullWidth={true}
    />
    <CustomTextField
      label="Description"
      name="description"
      guide="Describe your Procedure Type"
      required={false}
      multi={3}
      fullWidth={true}
    />
    <ColorsWithPreview />
  </>
);

import {EXERCISE_TYPE_NAME} from "../Incidents/Utils/incidentUtils";

export const IncidentTypesSchemaFields = ({itemData, isAdd}) => {
  const {responseWorkflowName} = useOrgSettings();
  const isExerciseType = !isAdd && itemData?.name === EXERCISE_TYPE_NAME;

  return (
    <>
      <CustomTextField
        label="Name"
        name="name"
        guide={`Name your ${responseWorkflowName.upper} Type`}
        required={true}
        multi={1}
        fullWidth={true}
        isDisabled={isExerciseType}
      />
      <CustomTextField
        label="Description"
        name="description"
        guide={`Describe your ${responseWorkflowName.upper} Type`}
        required={false}
        multi={3}
        fullWidth={true}
      />
      <ColorsWithPreview />
    </>
  );
};

IncidentTypesSchemaFields.propTypes = {
  itemData: PropTypes.object,
  isAdd: PropTypes.bool,
};

export const TaskTypesSchemaFields = () => (
  <>
    <CustomTextField
      label="Name"
      name="name"
      guide="Name your Task Type"
      required={true}
      multi={1}
      fullWidth={true}
    />
    <CustomTextField
      label="Description"
      name="description"
      guide="Describe your Task Type"
      required={false}
      multi={3}
      fullWidth={true}
    />
    <ColorsWithPreview />
  </>
);

export const IncidentSeveritySchemaFields = () => {
  const {responseWorkflowName} = useOrgSettings();
  return (
    <>
      <CustomTextField
        label="Severity Name"
        name="name"
        guide={`Name your ${responseWorkflowName.upper} Severity`}
        required={true}
        multi={1}
        fullWidth={true}
      />
      <CustomTextField
        label="Description"
        name="description"
        guide={`Describe your ${responseWorkflowName.upper} Severity`}
        required={false}
        multi={5}
        fullWidth={true}
      />
      <ColorsWithPreview />
    </>
  );
};

export const IncidentPhaseSchemaFields = () => {
  const {responseWorkflowName} = useOrgSettings();
  return (
    <>
      <CustomTextField
        label={`${responseWorkflowName.upper} Phase Name`}
        name="name"
        guide={`Name your ${responseWorkflowName.upper} Phase`}
        required={true}
        fullWidth={true}
      />
      <CustomTextField
        label="Description"
        name="description"
        guide={`Describe your ${responseWorkflowName.upper} Phase`}
        required={false}
        multi={3}
        fullWidth={true}
      />
      <ColorsWithPreview />
    </>
  );
};

export const ActionPhaseSchemaFields = () => (
  <>
    <CustomTextField
      label="Action Phase Name"
      name="name"
      guide="Name your Action Phase"
      required={true}
      multi={1}
      fullWidth={true}
    />
    <CustomTextField
      label="Description"
      name="description"
      guide="Describe your Action Phase"
      required={false}
      multi={3}
      fullWidth={true}
    />
    <ColorsWithPreview />
  </>
);

export const TagSchemaFields = () => (
  <>
    <CustomTextField
      label="Tag Name"
      name="name"
      guide="Name your Tag"
      required={true}
      fullWidth={true}
    />
  </>
);

export const MetricTypesSchemaFields = ({measureAgainstTypesData}) => {
  const {responseWorkflowName} = useOrgSettings();
  return (
    <>
      <CustomTextField
        label="Name"
        name="name"
        guide="Name your Metric Type"
        required={true}
        multi={1}
        fullWidth={true}
      />
      <CustomTextField
        label="Description"
        name="description"
        guide="Describe your Metric Type"
        required={false}
        multi={3}
        fullWidth={true}
      />
      <CustomSelect
        label="Measure Against"
        name="measureAgainst"
        guide="Choose what this metric measures against"
        required={true}
        optionLabel="display"
        options={map(
          measureAgainstTypesData?.measureAgainstTypes?.enumValues,
          (item) => {
            return {
              ...item,
              display: metricMeasureAgainstDisplay(
                item.name,
                responseWorkflowName,
              ),
            };
          },
        )}
      />
    </>
  );
};
